import React, { useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, BlockedDomain, SubscriptionTier } from '../lib/api';
import { Loader2, Plus, ShieldCheck, ShieldOff, ChevronDown } from 'lucide-react';

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/**
 * Signup domains (28 Sep, mig 0702).
 *
 * This page was "Plans & Rates": subscription tiers priced in money, a base
 * USD/INR rate per credit and the annual discount. The owner retired all of
 * it — "You only operate on credits" — so what is left here is the one thing
 * that was never about money: the personal-email domains signup rejects.
 * The retired tiers stay listed, read-only and collapsed, so the payments
 * made against them before 28 Sep still explain themselves.
 */
export const Plans: React.FC = () => (
  <div className="max-w-5xl space-y-6">
    <div className="flex items-center space-x-3">
      <ShieldOff className="w-7 h-7 text-blue-600" />
      <h1 className="text-2xl font-bold text-gray-900">Signup domains</h1>
    </div>
    <DomainsPanel />
    <RetiredPlans />
  </div>
);

/** Read-only history: the tiers customers paid for before plans were retired. */
const RetiredPlans: React.FC = () => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [open, setOpen] = useState(false);
  const [tiers, setTiers] = useState<SubscriptionTier[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || tiers) return;
    api.getSubscriptionTiers().then(setTiers).catch(e => setError(errorText(e)));
  }, [open]);

  return (
    <details className="bg-white border border-gray-200 rounded-lg" onToggle={e => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 flex items-center gap-2 list-none">
        <ChevronDown className="w-4 h-4 text-gray-400" /> Retired plans (history)
      </summary>
      <div className="px-4 pb-4 text-sm text-gray-600 space-y-2">
        <p>Plans were retired on 28 Sep. Organisations now run on credits: a price per lead and a success fee, set on the rate card. These rows are kept so earlier payments stay explainable.</p>
        {error && <Banner kind="error">{error}</Banner>}
        {!tiers && !error && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        {tiers && tiers.length === 0 && <p className="text-gray-400">No plans were ever set up.</p>}
        {tiers && tiers.length > 0 && (
          <table className="min-w-full text-sm border border-gray-200 rounded">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr><th className="text-left px-3 py-2">Plan</th><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Region</th><th className="text-right px-3 py-2">Credits</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tiers.map(t => (
                <tr key={t.id}>
                  <td className="px-3 py-2 text-gray-900">{t.display_name}</td>
                  <td className="px-3 py-2 font-mono text-gray-500">{t.code}</td>
                  <td className="px-3 py-2 text-gray-500">{t.region === 'india' ? 'India' : 'Global'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{(t.credits ?? 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
};

const DomainsPanel: React.FC = () => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [rows, setRows] = useState<BlockedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows(await api.getBlockedDomains()); setError(null); }
    catch (e) { setError(`Couldn't load the blocked domains: ${errorText(e)}. Refresh to try again.`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    setBusy(true);
    try { await api.addBlockedDomain(domain); setNewDomain(''); await load(); }
    catch (e) { setError(`Couldn't block ${domain}: ${errorText(e)}`); }
    finally { setBusy(false); }
  };
  const allow = async (domain: string) => {
    if (!window.confirm(`Allow signups from ${domain}?`)) return;
    setBusy(true);
    try { await api.removeBlockedDomain(domain); await load(); }
    catch (e) { setError(`Couldn't allow ${domain}: ${errorText(e)}`); }
    finally { setBusy(false); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-gray-400" />;
  return (
    <div className="space-y-4">
      {error && <Banner kind="error">{error}</Banner>}
      <p className="text-sm text-gray-600 flex items-center gap-2"><ShieldOff className="w-4 h-4 text-gray-400" /> Signups from these personal-email domains are rejected.</p>
      <div className="flex gap-2">
        <input className="border rounded px-3 py-2 w-72" placeholder="gmail.com" value={newDomain}
          onChange={e => setNewDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} />
        <button onClick={add} disabled={busy} className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 disabled:opacity-50"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr><th className="text-left px-3 py-2">Domain</th><th className="text-left px-3 py-2">Status</th><th /></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(d => (
            <tr key={d.domain} className="bg-white">
              <td className="px-3 py-2 font-mono text-gray-900">{d.domain}</td>
              <td className="px-3 py-2">{d.is_active ? 'Blocked' : 'Allowed'}</td>
              <td className="px-3 py-2 text-right">{d.is_active && (
                <button onClick={() => allow(d.domain)} disabled={busy} title="Allow signups from this domain" aria-label={`Allow signups from ${d.domain}`}
                  className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 disabled:opacity-50"><ShieldCheck className="w-4 h-4" /> Allow</button>
              )}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr className="bg-white"><td colSpan={3} className="px-3 py-4 text-center text-gray-400">No blocked domains yet. Add one above to reject signups from it.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const Banner: React.FC<{ kind: 'error' | 'info'; children: React.ReactNode }> = ({ kind, children }) => (
  <div className={`px-4 py-3 rounded text-sm border ${
    kind === 'error'
      ? 'bg-red-50 border-red-200 text-red-700'
      : 'bg-blue-50 border-blue-200 text-blue-700'
  }`}>
    {children}
  </div>
);

export default Plans;
